import { useRef, useState } from "react";
import axios from "axios";
import { UploadCloud, CheckCircle2, X, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const readFile = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve({
        filename: file.name,
        contentType: file.type || "application/octet-stream",
        base64: result.split(",")[1] || "",
        preview: file.type.startsWith("image/") ? result : null,
      });
    };
    reader.readAsDataURL(file);
  });

export default function DocumentRow({ doc, token, index, onUploaded }) {
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState(doc.note || "");
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  const addFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    const read = await Promise.all(arr.map(readFile));
    setFiles((prev) => [...prev, ...read]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const removeFile = (i) =>
    setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const upload = async () => {
    if (!files.length || uploading) return;
    setUploading(true);
    try {
      await axios.post(`${API}/syam/document`, {
        token,
        recordId: doc.recordId,
        note,
        files: files.map((f) => ({
          filename: f.filename,
          contentType: f.contentType,
          base64: f.base64,
        })),
      });
      setDone(true);
      onUploaded(doc.recordId);
      toast.success(`Uploaded — ${doc.document}`, {
        description: "Received by SyamCPA ✓",
      });
    } catch (e) {
      toast.error("Upload failed", {
        description: "Check your connection and try again.",
      });
    } finally {
      setUploading(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-2xl border border-green-200 bg-green-50/60 p-4 flex items-start gap-3"
        data-testid={`doc-received-${index}`}
      >
        <CheckCircle2 className="w-5 h-5 text-[#16a34a] shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-[14px] text-[#14532d] line-through decoration-green-300/70">
            {doc.document}
          </p>
          <p className="text-[12px] text-green-700 mt-0.5">
            {files.length} file{files.length === 1 ? "" : "s"} received
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
      data-testid={`doc-row-${index}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="font-semibold text-[14px] text-gray-900 leading-snug">
            {doc.document}
          </p>
          {doc.type && (
            <span className="inline-block mt-1.5 text-[11px] font-medium rounded-full px-2.5 py-0.5 bg-gray-100 text-gray-600">
              {doc.type}
            </span>
          )}
        </div>
      </div>

      {/* dropzone */}
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        data-testid={`doc-dropzone-${index}`}
        className="mt-3 rounded-xl border-2 border-dashed cursor-pointer transition-colors flex flex-col items-center justify-center py-6 px-3 text-center"
        style={{
          borderColor: dragging ? "#16a34a" : "#d1d5db",
          backgroundColor: dragging ? "#f0fdf4" : "#fafafa",
        }}
      >
        <UploadCloud className="w-6 h-6 text-[#16a34a]" />
        <p className="text-[13px] text-gray-600 mt-1.5 font-medium">
          Tap to choose or drag files here
        </p>
        <p className="text-[11px] text-gray-400">PDF or images · multiple allowed</p>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
        data-testid={`doc-file-input-${index}`}
      />

      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid={`doc-thumbs-${index}`}>
          {files.map((f, i) => (
            <div key={i} className="relative">
              {f.preview ? (
                <img
                  src={f.preview}
                  alt={f.filename}
                  className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg border border-gray-200 bg-gray-50 flex flex-col items-center justify-center text-[9px] text-gray-500 px-1 gap-1">
                  <FileText className="w-5 h-5 text-gray-400" />
                  PDF
                </div>
              )}
              <button
                type="button"
                onClick={() => removeFile(i)}
                data-testid={`doc-remove-file-${index}-${i}`}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gray-700 text-white flex items-center justify-center shadow"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="Add a note (optional)"
        data-testid={`doc-note-${index}`}
        className="mt-3 w-full px-3 py-2 rounded-xl border border-gray-200 bg-gray-50 text-[13px] outline-none focus:border-[#16a34a] resize-none"
      />

      <button
        type="button"
        onClick={upload}
        disabled={!files.length || uploading}
        data-testid={`doc-upload-btn-${index}`}
        className="mt-3 w-full h-11 rounded-xl font-semibold text-white text-[14px] flex items-center justify-center gap-2 transition-colors disabled:opacity-40 active:scale-[0.99]"
        style={{ backgroundColor: "#16a34a" }}
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
          </>
        ) : (
          `Upload${files.length ? ` ${files.length} file${files.length === 1 ? "" : "s"}` : ""}`
        )}
      </button>
    </div>
  );
}
